import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { EditPropertyData } from '@/schema/properties';

interface ContactInformationProps {
  form: UseFormReturn<EditPropertyData>;
}

export function ContactInformation({ form }: ContactInformationProps) {
  const {
    control,
    formState: { errors },
  } = form;
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  return (
    <div className="space-y-4">
      <h3 className={cn('text-lg font-medium', isRTL && 'text-right')}>
        {t('properties.edit.contactInfo.title')}
      </h3>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="contact_person" className={cn(isRTL && 'text-right')}>
            {t('properties.edit.contactInfo.contactPerson')} *
          </Label>
          <Controller
            name="contact_person"
            control={control}
            render={({ field }) => (
              <Input
                id="contact_person"
                {...field}
                className={cn(
                  errors.contact_person ? 'border-destructive' : '',
                  isRTL ? 'text-right' : 'text-left'
                )}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            )}
          />
          {errors.contact_person && (
            <p
              className={cn('text-sm text-destructive', isRTL && 'text-right')}
            >
              {errors.contact_person.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label
              htmlFor="contact_email"
              className={cn(isRTL && 'text-right')}
            >
              {t('properties.edit.contactInfo.contactEmail')} *
            </Label>
            <Controller
              name="contact_email"
              control={control}
              render={({ field }) => (
                <Input
                  id="contact_email"
                  type="email"
                  {...field}
                  className={cn(
                    errors.contact_email ? 'border-destructive' : '',
                    isRTL ? 'text-right' : 'text-left'
                  )}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              )}
            />
            {errors.contact_email && (
              <p
                className={cn(
                  'text-sm text-destructive',
                  isRTL && 'text-right'
                )}
              >
                {errors.contact_email.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="contact_phone"
              className={cn(isRTL && 'text-right')}
            >
              {t('properties.edit.contactInfo.contactPhone')} *
            </Label>
            <Controller
              name="contact_phone"
              control={control}
              render={({ field }) => (
                <Input
                  id="contact_phone"
                  {...field}
                  className={cn(
                    errors.contact_phone ? 'border-destructive' : '',
                    isRTL ? 'text-right' : 'text-left'
                  )}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              )}
            />
            {errors.contact_phone && (
              <p
                className={cn(
                  'text-sm text-destructive',
                  isRTL && 'text-right'
                )}
              >
                {errors.contact_phone.message}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
